import { useSession, useTenant } from '@zhiyue/core';
import { Alert, Button, Space } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { getReceiptRequestResult } from '@/services/bff/receipt-workflow.api';
import type {
  ReceiptPlan,
  ReceiptRegistration,
} from '@/services/bff/types/receipt-workflow';

type Pending = { id: string; center: 'contract' | 'finance'; label: string };
export function useReceiptCommand(onRecovered: () => void) {
  const { currentTenantUuid } = useTenant();
  const { currentUser } = useSession();
  const storageKey = `crm-receipt-request:${currentTenantUuid}:${currentUser?.memberUuid}`;
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const retry = useRef<null | (() => Promise<unknown>)>(null);
  const activeKey = useRef(storageKey);
  activeKey.current = storageKey;
  useEffect(() => {
    activeKey.current = storageKey;
    const stored = sessionStorage.getItem(storageKey);
    try {
      setPending(stored ? JSON.parse(stored) : null);
    } catch {
      setPending(null);
    }
    setBusy(false);
    setError('');
    retry.current = null;
    return () => {
      activeKey.current = '';
    };
  }, [storageKey]);
  const clear = () => {
    sessionStorage.removeItem(storageKey);
    setPending(null);
    retry.current = null;
  };
  const execute = async <T,>(
    label: string,
    center: Pending['center'],
    operation: (id: string) => Promise<T>,
  ): Promise<T> => {
    if (
      pending ||
      busy ||
      inFlight.current ||
      sessionStorage.getItem(storageKey)
    )
      throw new Error('请先查询上一笔请求的处理结果');
    inFlight.current = true;
    const key = storageKey;
    const id = crypto.randomUUID();
    const next = { id, center, label };
    sessionStorage.setItem(key, JSON.stringify(next));
    setPending(next);
    setBusy(true);
    setError('');
    const run = () => operation(id);
    retry.current = run;
    try {
      const result = await run();
      if (activeKey.current !== key)
        throw new Error('企业或成员已切换，请在原企业查询请求结果');
      clear();
      return result;
    } catch (cause) {
      if (activeKey.current === key) {
        const status = (cause as { response?: { status?: number } }).response
          ?.status;
        if (status && [400, 401, 403, 404, 409, 422].includes(status)) clear();
        setError(
          cause instanceof Error ? cause.message : '操作失败，请查询处理结果',
        );
      }
      throw cause;
    } finally {
      inFlight.current = false;
      if (activeKey.current === key) setBusy(false);
    }
  };
  const recover = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const result = await getReceiptRequestResult(pending.id, pending.center);
      if (result.status === 'SUCCEEDED' || result.status === 'FAILED') {
        clear();
        setError(
          result.status === 'FAILED'
            ? result.message || '原请求未执行，可修改后重试'
            : '',
        );
        onRecovered();
      } else
        setError(
          result.status === 'NOT_FOUND'
            ? '尚未查到请求。请稍后查询或使用相同请求编号重试原请求。'
            : '上游仍在处理，请稍后查询。',
        );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '查询失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  };
  const retryOriginal = async () => {
    if (!retry.current) return;
    setBusy(true);
    try {
      await retry.current();
      clear();
      setError('');
      onRecovered();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '原请求仍未确认');
    } finally {
      setBusy(false);
    }
  };
  const feedback = pending ? (
    <Alert
      type="warning"
      showIcon
      title={`${pending.label}：结果待确认`}
      description={
        <Space orientation="vertical">
          <span>请求编号：{pending.id}。查询确认前暂停新的写操作。</span>
          {error && <span>{error}</span>}
          <Space>
            <Button loading={busy} onClick={() => void recover()}>
              查询处理结果
            </Button>
            {retry.current && (
              <Button disabled={busy} onClick={() => void retryOriginal()}>
                重试原请求
              </Button>
            )}
          </Space>
        </Space>
      }
    />
  ) : error ? (
    <Alert type="error" showIcon title={error} />
  ) : null;
  return { execute, busy, blocked: Boolean(pending), feedback };
}
export type ReceiptCommand = ReturnType<typeof useReceiptCommand>;
